import { uploadTempFile, isValidFileType, getFileSizeMB } from './supabaseStorage'

const KIE_BASE_URL = 'https://api.kie.ai'

const getApiKey = () => {
  return localStorage.getItem('kie_api_key')
}

export const generateKieVideo = async ({
  prompt,
  aspectRatio,
  model,
  image,
  mode = 'normal',
  videoUrl,
  duration,
  resolution,
  negativePrompt,
  enablePromptExpansion,
  seed
}) => {
  const apiKey = getApiKey()

  if (!apiKey) {
    throw new Error('API key not set. Please configure your Kie.AI API key in settings.')
  }

  const modelMap = {
    'grok-imagine-text': 'grok-imagine/text-to-video',
    'grok-imagine-image': 'grok-imagine/image-to-video',
    'runway-aleph': 'runway-aleph/video-to-video',
    'wan-text': 'wan/2-5-text-to-video',
    'wan-image': 'wan/2-5-image-to-video',
    'hailuo-pro': 'hailuo/2-3-image-to-video-pro',
    'hailuo-standard': 'hailuo/2-3-image-to-video-standard',
  }

  const mappedModel = modelMap[model] || 'grok-imagine/text-to-video'

  let requestBody = {}
  let endpoint = ''

  if (model === 'runway-aleph') {
    endpoint = `${KIE_BASE_URL}/api/v1/aleph/generate`

    if (!videoUrl) {
      throw new Error('Video URL is required for Runway Aleph. Please provide a publicly accessible video URL.')
    }

    requestBody = {
      prompt: prompt,
      videoUrl: videoUrl,
      waterMark: '',
      uploadCn: false
    }

    if (aspectRatio) {
      const aspectRatioMap = {
        'portrait': '9:16',
        'landscape': '16:9',
        'square': '1:1'
      }
      requestBody.aspectRatio = aspectRatioMap[aspectRatio] || '16:9'
    }
  } else if (model === 'wan-text' || model === 'wan-image') {
    endpoint = `${KIE_BASE_URL}/api/v1/jobs/createTask`

    requestBody = {
      model: mappedModel,
      input: {
        prompt: prompt
      }
    }

    if (duration) {
      requestBody.input.duration = duration.toString()
    }

    if (resolution) {
      requestBody.input.resolution = resolution
    }

    if (model === 'wan-text' && aspectRatio) {
      const aspectRatioMap = {
        'portrait': '9:16',
        'landscape': '16:9',
        'square': '1:1'
      }
      requestBody.input.aspect_ratio = aspectRatioMap[aspectRatio] || '16:9'
    }

    if (negativePrompt && negativePrompt.trim()) {
      requestBody.input.negative_prompt = negativePrompt.trim()
    }

    if (enablePromptExpansion !== undefined) {
      requestBody.input.enable_prompt_expansion = enablePromptExpansion
    }

    if (seed !== undefined && seed !== null && seed !== '') {
      requestBody.input.seed = parseInt(seed)
    }

    // Handle image upload to Supabase for Wan image-to-video
    if (model === 'wan-image' && image) {
      try {
        console.log('📤 Uploading image to Supabase Storage...')
        console.log('File size:', getFileSizeMB(image), 'MB')

        if (!isValidFileType(image, 'image')) {
          throw new Error('Invalid file type. Please upload a valid image (JPEG, PNG, WebP)')
        }

        const imageUrl = await uploadTempFile(image, 'image')
        
        console.log('✅ Image uploaded successfully!')
        console.log('Public URL:', imageUrl)

        requestBody.input.image_url = imageUrl
      } catch (error) {
        console.error('❌ Failed to upload image:', error)
        throw new Error('Failed to upload image: ' + error.message)
      }
    }
  } else if (model === 'hailuo-pro' || model === 'hailuo-standard') {
    endpoint = `${KIE_BASE_URL}/api/v1/jobs/createTask`

    requestBody = {
      model: mappedModel,
      input: {
        prompt: prompt
      }
    }

    if (duration) {
      requestBody.input.duration = duration.toString()
    }

    if (resolution) {
      requestBody.input.resolution = resolution
    }

    // Handle image upload to Supabase for Hailuo
    if (image) {
      try {
        console.log('📤 Uploading image to Supabase Storage...')
        console.log('File size:', getFileSizeMB(image), 'MB')

        if (!isValidFileType(image, 'image')) {
          throw new Error('Invalid file type. Please upload a valid image (JPEG, PNG, WebP)')
        }

        const imageUrl = await uploadTempFile(image, 'image')
        
        console.log('✅ Image uploaded successfully!')
        console.log('Public URL:', imageUrl)

        requestBody.input.image_url = imageUrl
      } catch (error) {
        console.error('❌ Failed to upload image:', error)
        throw new Error('Failed to upload image: ' + error.message)
      }
    } else {
      throw new Error('Image is required for Hailuo 2.3 models')
    }
  } else {
    // Grok Imagine models
    endpoint = `${KIE_BASE_URL}/api/v1/jobs/createTask`

    requestBody = {
      model: mappedModel,
      input: {}
    }

    if (prompt) {
      requestBody.input.prompt = prompt
    }

    if (aspectRatio && mappedModel === 'grok-imagine/text-to-video') {
      const aspectRatioMap = {
        'portrait': '2:3',
        'landscape': '3:2',
        'square': '1:1'
      }
      requestBody.input.aspect_ratio = aspectRatioMap[aspectRatio] || '2:3'
    }

    requestBody.input.mode = mode

    // Handle image upload to Supabase for Grok Imagine image-to-video
    if (image && mappedModel === 'grok-imagine/image-to-video') {
      try {
        console.log('📤 Uploading image to Supabase Storage...')
        console.log('File size:', getFileSizeMB(image), 'MB')

        if (!isValidFileType(image, 'image')) {
          throw new Error('Invalid file type. Please upload a valid image (JPEG, PNG, WebP)')
        }

        const imageUrl = await uploadTempFile(image, 'image')
        
        console.log('✅ Image uploaded successfully!')
        console.log('Public URL:', imageUrl)

        requestBody.input.image_urls = [imageUrl]
      } catch (error) {
        console.error('❌ Failed to upload image:', error)
        throw new Error('Failed to upload image: ' + error.message)
      }
    }
  }

  console.log('=== KIE.AI GENERATION REQUEST ===')
  console.log('URL:', endpoint)
  console.log('Model:', model)
  console.log('Has Image:', !!image)
  console.log('Has Video URL:', !!videoUrl)
  console.log('Request Body:', JSON.stringify(requestBody, null, 2))

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify(requestBody)
    })

    console.log('Response Status:', response.status)

    if (!response.ok) {
      const errorText = await response.text()
      let errorData = {}
      try {
        errorData = JSON.parse(errorText)
      } catch (e) {
        errorData = { message: errorText }
      }

      console.error('Generation Error Response:', errorData)

      const errorMessage = errorData.message || errorData.msg || ''

      if (model === 'runway-aleph' && errorMessage.includes('Server exception')) {
        throw new Error(
          'Runway Aleph video-to-video failed.\n\n' +
          'Please ensure:\n' +
          '• The URL is publicly accessible via HTTPS\n' +
          '• The URL points directly to a video file (.mp4, .mov, .webm)\n' +
          '• The video is hosted on a reliable service\n\n' +
          'Try uploading your video to a service like Google Drive, Dropbox, or ImgBB first.'
        )
      }

      throw new Error(errorMessage || `API Error: ${response.status} - ${response.statusText}`)
    }

    const data = await response.json()
    console.log('=== GENERATION RESPONSE ===')
    console.log(JSON.stringify(data, null, 2))

    if (data.code !== undefined && data.code !== 200 && data.code !== 0) {
      throw new Error(data.msg || data.message || 'Video generation failed')
    }

    return {
      taskId: data.data?.taskId || data.taskId,
      status: 'pending'
    }
  } catch (error) {
    console.error('=== GENERATION ERROR ===')
    console.error('Error:', error)
    throw error
  }
}

export const checkKieVideoStatus = async (taskId, model) => {
  const apiKey = getApiKey()

  if (!apiKey) {
    throw new Error('API key not set. Please configure your Kie.AI API key in settings.')
  }

  let statusUrl = ''
  if (model === 'runway-aleph') {
    statusUrl = `${KIE_BASE_URL}/api/v1/aleph/record-info?taskId=${taskId}`
  } else {
    statusUrl = `${KIE_BASE_URL}/api/v1/jobs/recordInfo?taskId=${taskId}`
  }

  try {
    const response = await fetch(statusUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      const errorText = await response.text()
      let errorData = {}
      try {
        errorData = JSON.parse(errorText)
      } catch (e) {
        errorData = { message: errorText }
      }

      throw new Error(
        errorData.message || errorData.msg || `HTTP error! status: ${response.status}`
      )
    }

    const responseData = await response.json()
    const data = responseData.data
    let status, progress, videoUrl, error

    if (model === 'runway-aleph') {
      const successFlag = data.successFlag

      if (successFlag === 1) {
        status = 'completed'
        progress = 100
        videoUrl = data.response?.resultVideoUrl
      } else if (successFlag === 0 && data.errorMessage) {
        status = 'failed'
        progress = 0
        error = data.errorMessage
      } else {
        status = 'processing'
        progress = 50
      }
    } else {
      const stateMap = {
        'waiting': 'pending',
        'queuing': 'pending',
        'generating': 'processing',
        'success': 'completed',
        'fail': 'failed'
      }

      status = stateMap[data.state] || data.state

      if (data.state === 'waiting') progress = 10
      else if (data.state === 'queuing') progress = 25
      else if (data.state === 'generating') progress = 60
      else if (data.state === 'success') progress = 100
      else if (data.state === 'fail') progress = 0

      if (data.resultJson) {
        try {
          const result = JSON.parse(data.resultJson)
          if (result.resultUrls && Array.isArray(result.resultUrls) && result.resultUrls.length > 0) {
            videoUrl = result.resultUrls[0]
          }
        } catch (e) {
          console.error('Failed to parse resultJson:', e)
        }
      }

      if (data.state === 'fail') {
        error = data.failMsg || 'Generation failed'
      }
    }

    return {
      status: status,
      progress: progress,
      url: videoUrl,
      error: error,
      _raw: responseData
    }
  } catch (error) {
    console.error('=== STATUS CHECK ERROR ===')
    console.error('Error:', error)
    throw error
  }
}